/**
 * Datadog Browser SDK initialization (RUM + Logs).
 *
 * This module initializes both @datadog/browser-logs (existing) and
 * @datadog/browser-rum (new) with end-to-end request correlation.
 *
 * Import this module once in index.jsx before rendering. It runs as a side-effect:
 *   import './lib/datadog';
 *
 * Environment detection:
 *   - Production: lightningpayadmin.propark.com → service=lightning-pay-admin-browser, env=prod
 *   - Staging: any deployed non-production domain → service=lpa-staging-browser, env=staging
 *   - Localhost / 127.0.0.1 / ngrok: SDK not initialized (no noise in Datadog from local development)
 *
 * Request correlation:
 *   A session-scoped request_id (from src/lib/request-id.js) is set as global context
 *   on both RUM and Logs. The same request_id is sent as X-Request-Id header on every
 *   API call. The backend reads it and includes it in all Winston logs.
 *   → Search Datadog by request_id to see the full frontend + backend story.
 *
 * PII masking:
 *   The beforeSend callbacks (both Logs and RUM) run maskPIIinObject on every event
 *   before it leaves the browser. Emails and phone numbers are masked.
 *
 * Version:
 *   VITE_APP_VERSION is injected at build time by vite.config.js from package.json.
 */

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { maskPIIinObject } from '@/lib/mask-pii';
import { getRequestId } from '@/lib/request-id';
import { scrubSensitiveUrlFromEvent } from '@/lib/scrub-sensitive-data';

// --- Environment detection ---
const domain = window.location.hostname;
const isProduction = domain === 'lightningpayadmin.propark.com';
const isLocalOrNgrok = domain === 'localhost' || domain === '127.0.0.1' || domain.includes('ngrok');

// --- Configuration ---
const clientToken = import.meta.env.VITE_DATADOG_LOG_TOKEN;
const applicationId = import.meta.env.VITE_DD_APPLICATION_ID;
const env = isProduction ? 'prod' : 'staging';
const service = isProduction ? 'lightning-pay-admin-browser' : 'lpa-staging-browser';

// VITE_APP_VERSION is injected at build time by vite.config.js define — no runtime declaration needed.
/* global VITE_APP_VERSION */
const version = VITE_APP_VERSION;

// Generate request_id once for correlation across all Datadog products
const requestId = getRequestId();
let rumInitialized = false;

// --- beforeSend callbacks ---
// These run on every event before it's sent to Datadog.
// They mask PII and strip unnecessary data to reduce cost and protect privacy.

const beforeSend = (event) => {
  // Strip geo data (enriched server-side from IP — not needed, reduces event size)
  delete event.geo;

  // Mask PII data before sending to Datadog
  const maskedEvent = maskPIIinObject(event);

  // Update the original event with masked data.
  Object.assign(event, maskedEvent);
  scrubSensitiveUrlFromEvent(event);

  return true; // Send the log
};

const beforeSendRum = (event) => {
  // Strip geo data (enriched server-side from IP — not needed, reduces event size)
  delete event.geo;

  if (event.context && typeof event.context === 'object') {
    event.context = maskPIIinObject(event.context);
  }
  if (event.usr && typeof event.usr === 'object') {
    event.usr = maskPIIinObject(event.usr);
  }
  scrubSensitiveUrlFromEvent(event);

  return true;
};

// --- Initialize Browser Logs ---
// Only initialize if we have a client token and we're not on localhost, 127.0.0.1, or ngrok.
// On localhost, 127.0.0.1, or ngrok hosts, redirect datadogLogs.logger to console for local debugging.
if (clientToken && !isLocalOrNgrok) {
  datadogLogs.init({
    clientToken,
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: 'all',
    service,
    env,
    version,
    sessionSampleRate: 100,
    beforeSend,
  });
  datadogLogs.setGlobalContextProperty('request_id', requestId);
  datadogLogs.setGlobalContextProperty('http_route', window.location.pathname);
} else if (clientToken && isLocalOrNgrok) {
  // In local development, redirect Datadog logger to console so logs appear in DevTools
  // without actually sending anything to Datadog.
  datadogLogs.logger = console;
}

// --- Initialize RUM ---
// Only initialize if we have both a client token and application ID,
// and we're not on localhost, 127.0.0.1, or ngrok hosts (no RUM sessions for local development).
if (clientToken && applicationId && !isLocalOrNgrok) {
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service,
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    propagateTraceBaggage: true,
    traceSampleRate: 100,
    traceContextInjection: 'all',
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: beforeSendRum,
    // Trace propagation: these headers are injected by the RUM SDK on outgoing
    // XHR/fetch requests to the backend. The backend (via OpenTelemetry auto-instrumentation)
    // reads them to correlate frontend RUM sessions with backend traces.
    allowedTracingUrls: [
      {
        match: (url) => {
          // Match the API base URL(s) for trace propagation.
          // Include download URLs because some dashboard/export requests use a separate function.
          const allowedUrls = [
            import.meta.env.VITE_FUNCTIONS_URL,
            import.meta.env.VITE_FUNCTIONS_URL_DEPLOY,
            import.meta.env.VITE_DOWNLOAD_URL_DEPLOY,
          ].filter(Boolean);
          return allowedUrls.some((allowedUrl) => url.startsWith(allowedUrl));
        },
        // Propagate Datadog and W3C tracecontext headers. The baggage header
        // is enabled separately via propagateTraceBaggage above.
        propagatorTypes: ['datadog', 'tracecontext'],
      },
    ],
  });

  rumInitialized = true;

  // Set request_id as global RUM context for correlation.
  // This appears in every RUM event and can be used to find all backend logs
  // for a specific frontend session.
  datadogRum.setGlobalContextProperty('request_id', requestId);
  datadogRum.setGlobalContextProperty('http_route', window.location.pathname);
}

const removeDatadogContextProperty = (key) => {
  datadogLogs.removeGlobalContextProperty?.(key);
  if (rumInitialized) {
    datadogRum.removeGlobalContextProperty?.(key);
  }
};

const setDatadogContextProperty = (key, value) => {
  if (value === undefined || value === null || value === '') {
    if (key !== 'request_id') {
      removeDatadogContextProperty(key);
    }
    return;
  }

  datadogLogs.setGlobalContextProperty?.(key, value);
  if (rumInitialized) {
    datadogRum.setGlobalContextProperty?.(key, value);
  }
};

export const setBrowserContextProperty = (key, value) => {
  setDatadogContextProperty(key, value);
};

export const removeBrowserContextProperty = (key) => {
  removeDatadogContextProperty(key);
};

export const setBrowserContext = (context = {}) => {
  for (const [key, value] of Object.entries(context)) {
    setDatadogContextProperty(key, value);
  }
};

export const setTransactionContext = (context = {}) => {
  setBrowserContext({
    http_route: window.location.pathname,
    ...context,
  });
};

const updateHttpRouteContext = () => {
  setDatadogContextProperty('http_route', window.location.pathname);
};

const wrapHistoryMethod = (methodName) => {
  const originalMethod = window.history[methodName];
  window.history[methodName] = function patchedHistoryMethod(...args) {
    const result = originalMethod.apply(this, args);
    updateHttpRouteContext();
    return result;
  };
};

if (!isLocalOrNgrok) {
  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');
  window.addEventListener('popstate', updateHttpRouteContext);
}
